import { Droplets, Fuel } from "lucide-react";
import type { MonthlySpendDayRow } from "@/lib/fuel-analytics";
import { cn } from "@/lib/cn";
import { formatCurrency, formatNumber } from "@/lib/utils";

type Props = {
  dayRows: MonthlySpendDayRow[];
  year: number;
  month: number;
};

function fillUpLabel(count: number): string {
  return `${formatNumber(count)} fill-up${count === 1 ? "" : "s"}`;
}

export function FuelDayByDayPanel({ dayRows, year, month }: Props) {
  const activeRows = dayRows.filter((row) => row.fillUps > 0);
  if (activeRows.length === 0) return null;

  const maxSpent = Math.max(...activeRows.map((row) => row.spent), 1);
  const totalSpent = activeRows.reduce((sum, row) => sum + row.spent, 0);
  const peakDay = activeRows.reduce((best, row) => (row.spent > best.spent ? row : best), activeRows[0]);

  return (
    <div className="flex h-full flex-col rounded-3xl bg-canvas p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Day-by-day</p>
          <p className="mt-0.5 text-xs text-muted">
            {activeRows.length} active {activeRows.length === 1 ? "day" : "days"} ·{" "}
            {formatCurrency(totalSpent)} total
          </p>
        </div>
        {peakDay ? (
          <span className="shrink-0 rounded-full bg-accent-subtle px-2.5 py-1 text-[10px] font-bold text-accent-foreground">
            Peak · Day {peakDay.day}
          </span>
        ) : null}
      </div>

      <div className="stagger-children mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5">
        {activeRows.map((row) => {
          const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(
            new Date(year, month, row.day)
          );
          const isPeak = peakDay.day === row.day;
          const spendShare = totalSpent ? Math.round((row.spent / totalSpent) * 100) : 0;
          const barWidth = Math.max(8, Math.round((row.spent / maxSpent) * 100));

          return (
            <div
              key={row.day}
              className={cn(
                "rounded-2xl border border-transparent bg-surface p-3 shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-shadow duration-300 hover:shadow-[var(--shadow-elevation)]",
                isPeak && "border-accent/20 bg-accent-subtle/40"
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-canvas",
                    isPeak && "bg-surface"
                  )}
                >
                  <span className="text-base font-extrabold leading-none tracking-tight">{row.day}</span>
                  <span className="mt-1 text-[10px] font-bold uppercase tracking-wide text-muted">{weekday}</span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted">
                        <Fuel className="h-3.5 w-3.5 shrink-0" />
                        {fillUpLabel(row.fillUps)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted">
                        <Droplets className="h-3.5 w-3.5 shrink-0" />
                        {formatNumber(row.litres)} L
                      </span>
                    </div>
                    <p className="shrink-0 text-right text-sm font-extrabold tracking-tight text-foreground">
                      {formatCurrency(row.spent)}
                    </p>
                  </div>

                  <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-canvas">
                    <div
                      className={cn("grow-bar h-full rounded-full", isPeak ? "bg-accent" : "bg-foreground/75")}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>

                  <p className="mt-1.5 text-[10px] font-semibold text-muted">
                    {spendShare}% of month spend
                    {row.fillUps > 0 ? ` · ${formatCurrency(Math.round(row.spent / row.fillUps))} avg/fill` : ""}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
